type CharacterLinkResult = {
  type: "link";
  characterID: string;
};

type SearchQueryResult = {
  type: "query";
  text: string;
  tags: string[];
};

type ParseResult = CharacterLinkResult | SearchQueryResult;

/**
 * Parses a character search query and returns the result as a `ParseResult` object.
 *
 * The `parseSearch` function takes a query string and returns an object with the following properties:
 * - `type`: Either 'link' or 'query'
 * - `characterID`: If `type` is 'link', this will be the ID of the character
 * - `text`: If `type` is 'query', this will be the cleaned-up search text
 * - `tags`: If `type` is 'query', this will be an array of tags extracted from the search text
 */
export function useParseCharacterSearch() {
  const parseTags = (text: string): Set<string> => {
    if (!text) return new Set();
    const tags = text
      .split(" ")
      .filter((word) => word.startsWith("#"))
      .map((tag) => tag.slice(1))
      .filter((tag) => tag.length > 0);
    return new Set(tags);
  };

  const parseSearch = (query: string): ParseResult => {
    const pattern = /^https?:\/\/[^/]+\/character\/(\d+)$/;
    const match = query.match(pattern);

    if (match) {
      return {
        type: "link",
        characterID: match[1]
      };
    }

    const tags = parseTags(query);
    const cleanedQuery = query
      .replace(/#[a-zA-Z0-9_-]+[a-zA-Z0-9]/g, "")
      .replace(/#/g, "")
      .trim();

    return {
      type: "query",
      text: cleanedQuery,
      tags: Array.from(tags)
    };
  };

  return { parseSearch };
}
